import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  CreateReservationRequest,
  UpdateReservationRequest,
  ReservationFilters,
  Reservation,
  BulkUpdateReservationsRequest,
} from "@/types/reservation";
import { reservationService } from "@/services/reservationService";
import { settlementKeys } from "@/components/pages/settlement/hooks/useSettlement";
import { roomKeys } from "./useRooms";
import { useSelectedHotelId } from "@/utils/hotelContext";

// Query keys
export const reservationKeys = {
  all: ["reservations"] as const,
  lists: () => [...reservationKeys.all, "list"] as const,
  list: (filters?: ReservationFilters, hotelId?: string | null) =>
    [...reservationKeys.lists(), { filters, hotelId }] as const,
  listByRoom: (hotelId: string, roomId: string) =>
    [...reservationKeys.all, "listByRoom", hotelId, roomId] as const,
  listByGuest: (hotelId: string, guestId: string) =>
    [...reservationKeys.all, "listByGuest", hotelId, guestId] as const,
  details: () => [...reservationKeys.all, "detail"] as const,
  detail: (id: string) => [...reservationKeys.details(), id] as const,
  stats: (hotelId: string) =>
    [...reservationKeys.all, "stats", hotelId] as const,
  byHotel: (hotelId: string) =>
    [...reservationKeys.all, "hotel", hotelId] as const,
};

const DASHBOARD_QUERY_KEY = ["dashboard"] as const;

const invalidateReservationRelatedQueries = (
  queryClient: QueryClient,
  options: {
    includeDashboard?: boolean;
    includeRooms?: boolean;
  } = {},
) => {
  queryClient.invalidateQueries({ queryKey: reservationKeys.all });
  queryClient.invalidateQueries({ queryKey: settlementKeys.all });

  if (options.includeRooms) {
    queryClient.invalidateQueries({ queryKey: roomKeys.all });
  }

  if (options.includeDashboard) {
    queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
  }
};

// Hook to get reservations with pagination and filters
export const useReservations = (
  page: number = 1,
  limit: number = 10,
  filters?: ReservationFilters,
) => {
  const selectedHotelId = useSelectedHotelId();

  return useQuery({
    queryKey: reservationKeys.list(filters, selectedHotelId),
    queryFn: () => reservationService.getReservations(page, limit, filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useReservationsByRoom = (hotelId: string, roomId?: string) => {
  return useQuery({
    queryKey: reservationKeys.listByRoom(hotelId, roomId!),
    queryFn: () => reservationService.getReservationsByRoom(hotelId, roomId!),
    enabled: !!roomId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useReservationsByGuest = (hotelId: string, guestId: string) => {
  return useQuery({
    queryKey: reservationKeys.listByGuest(hotelId, guestId),
    queryFn: () => reservationService.getReservationsByGuest(hotelId, guestId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to get a single reservation by ID
export const useReservation = <T>(
  id?: string,
  onSelect?: (data: Reservation) => T,
) => {
  return useQuery({
    queryKey: reservationKeys.detail(id!),
    queryFn: () => reservationService.getReservationById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...(onSelect ? { select: onSelect } : {}),
  });
};

// Hook to create a new reservation
export const useCreateReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateReservationRequest) =>
      reservationService.createReservation(request),
    onSuccess: (createdReservation) => {
      queryClient.setQueryData(
        reservationKeys.detail(createdReservation.id),
        createdReservation,
      );
      invalidateReservationRelatedQueries(queryClient, {
        includeDashboard: true,
        includeRooms: true,
      });
    },
  });
};

// Hook to update a reservation
export const useAppendBookingNote = (bookingId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (content: string) =>
      reservationService.appendBookingNote(bookingId, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reservationKeys.all });
    },
  });
};

export const useUpdateReservation = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateReservationRequest) =>
      reservationService.updateReservation(id, request),
    onSuccess: (updatedReservation) => {
      queryClient.setQueryData(
        reservationKeys.detail(updatedReservation.id),
        updatedReservation,
      );
      invalidateReservationRelatedQueries(queryClient, {
        includeDashboard: true,
        includeRooms: true,
      });
    },
  });
};

export const useUpdateReservationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...request
    }: UpdateReservationRequest & { id: string }) =>
      reservationService.updateReservation(id, request),
    onSuccess: (updatedReservation: Reservation) => {
      queryClient.setQueryData(
        reservationKeys.detail(updatedReservation.id),
        updatedReservation,
      );
      invalidateReservationRelatedQueries(queryClient, {
        includeDashboard: true,
        includeRooms: true,
      });
    },
  });
};

export const useBulkUpdateReservations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ...request }: BulkUpdateReservationsRequest) =>
      reservationService.bulkUpdateReservations(request),
    onSuccess: () => {
      invalidateReservationRelatedQueries(queryClient, {
        includeDashboard: true,
        includeRooms: true,
      });
    },
  });
};

export const useBulkCancelReservations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bookingIds: string[]) =>
      Promise.all(
        bookingIds.map((bookingId) =>
          reservationService.deleteReservation(bookingId),
        ),
      ),
    onSuccess: () => {
      invalidateReservationRelatedQueries(queryClient, {
        includeDashboard: true,
        includeRooms: true,
      });
    },
  });
};
